import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { GoldButton } from "@/components/Buttons";
import { CONTACT_EMAIL, CONTACT_PHONE, CALENDAR_URL, SERVICE_AREAS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-ink-800 border border-white/10 text-white px-4 py-3 outline-none focus:border-gold transition-colors placeholder:text-cream/30";

const schema = z.object({
  name: z.string().min(2, "Please enter your name"),
  email: z.string().email("Please enter a valid email"),
  message: z.string().min(10, "Tell us a little more"),
});

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: "Prolific Maven",
  email: "info@prolificmaven.com",
  telephone: "+18137203565",
  url: "https://prolificmaven.com",
  address: { "@type": "PostalAddress", addressLocality: "Parrish", addressRegion: "FL", addressCountry: "US" },
  areaServed: ["Bradenton, FL", "Sarasota, FL", "Tampa, FL", "Manatee County, FL"],
};

export default function Contact() {
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm({ resolver: zodResolver(schema) });

  const onSubmit = async (data) => {
    try {
      await axios.post(`${API}/contact`, data);
      setSent(true);
      reset();
      toast.success("Message sent! We'll get back to you soon.");
    } catch {
      toast.error("Something went wrong. Please email us directly.");
    }
  };

  return (
    <>
      <Seo
        title="Contact Prolific Maven | Meta Ads Agency | Parrish, FL"
        description="Get in touch with Prolific Maven, Meta ads & brand voice agency serving Bradenton, Sarasota, Tampa & the Tampa Bay area. Email, call, or apply today."
        path="/contact"
        jsonLd={jsonLd}
      />
      <PageHero
        eyebrow="Contact"
        title="Let's talk about your market."
        subtitle="Have a question before applying? Reach out, we read every message."
      />

      <section className="relative z-10 bg-ink-800 border-t border-white/10 py-16 sm:py-24" data-testid="contact-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-8">
            <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-4 text-cream/80 hover:text-gold transition-colors" data-testid="contact-email-link">
              <Mail className="text-gold" size={22} /><span>{CONTACT_EMAIL}</span>
            </a>
            <a href="tel:+18137203565" className="flex items-center gap-4 text-cream/80 hover:text-gold transition-colors" data-testid="contact-phone-link">
              <Phone className="text-gold" size={22} /><span>{CONTACT_PHONE}</span>
            </a>
            <div className="flex items-start gap-4 text-cream/80">
              <MapPin className="text-gold shrink-0 mt-1" size={22} />
              <span>Based in Parrish, FL, serving local businesses within ~60 minutes: {SERVICE_AREAS.join(", ")}.</span>
            </div>
            <div className="pt-6 border-t border-white/10">
              <p className="text-cream/60 text-sm mb-4">Ready to take the next step?</p>
              <GoldButton href={CALENDAR_URL} testid="contact-apply">Apply to Work With Us →</GoldButton>
            </div>
          </div>

          <div className="lg:col-span-7">
            {sent ? (
              <div className="bg-ink-900 border border-gold/30 p-10 text-center" data-testid="contact-success">
                <h2 className="font-display font-bold text-2xl text-white">Thank you, message received.</h2>
                <p className="mt-3 text-cream/70">We'll get back to you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 bg-ink-900 border border-white/10 p-8 sm:p-10" data-testid="contact-form" noValidate>
                <div>
                  <label className="block text-sm text-cream/80 mb-2">Name <span className="text-gold">*</span></label>
                  <input data-testid="contact-name" className={inputCls} {...register("name")} />
                  {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name.message}</p>}
                </div>
                <div>
                  <label className="block text-sm text-cream/80 mb-2">Email <span className="text-gold">*</span></label>
                  <input data-testid="contact-email" type="email" className={inputCls} {...register("email")} />
                  {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email.message}</p>}
                </div>
                <div>
                  <label className="block text-sm text-cream/80 mb-2">Message <span className="text-gold">*</span></label>
                  <textarea data-testid="contact-message" rows={5} className={inputCls} {...register("message")} />
                  {errors.message && <p className="text-red-400 text-xs mt-1">{errors.message.message}</p>}
                </div>
                <button type="submit" disabled={isSubmitting} data-testid="contact-submit" className="inline-flex items-center gap-2 bg-gold text-ink-900 font-bold px-10 py-4 hover:bg-gold-light transition-colors disabled:opacity-60">
                  {isSubmitting ? "Sending…" : "Send Message"} <span aria-hidden>→</span>
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
